import palette from '@ui/palette';

/**
 * Shared matching logic for section search highlighting.
 *
 * Section search filters via fuse.js, which reports *which* sections matched
 * but not *where*. Rather than threading fuse's indices through - they are
 * computed against a snapshot taken on search bar focus, so they go stale as
 * soon as the user types - matches are recomputed here against whatever text
 * is being rendered right now. That keeps highlights correct by construction:
 * edit the matched text and the highlight disappears on the next render.
 */

export interface HighlightRange {
  start: number;
  /** Exclusive. */
  end: number;
}

/**
 * A single character matches too much of a typical script to be useful - it
 * would light up most of every section - so highlighting waits until the query
 * is specific enough to be worth showing.
 */
export const MIN_HIGHLIGHT_QUERY_LENGTH = 2;

/**
 * @description finds every case-insensitive occurrence of `query` in `text`.
 * Matches are plain substrings, not word-bounded, so a query of `ro 1.4`
 * highlights the tail of `Outro 1.4`.
 */
export const findHighlightRanges = (
  text: string,
  query: string
): HighlightRange[] => {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < MIN_HIGHLIGHT_QUERY_LENGTH || !text) return [];

  const haystack = text.toLowerCase();
  const needle = trimmedQuery.toLowerCase();
  const ranges: HighlightRange[] = [];

  let index = haystack.indexOf(needle);
  while (index !== -1) {
    ranges.push({ start: index, end: index + needle.length });
    index = haystack.indexOf(needle, index + needle.length);
  }

  return ranges;
};

export interface TextSegment {
  text: string;
  isMatch: boolean;
}

/**
 * @description splits `text` into alternating matched / unmatched segments for
 * rendering. Returns a single unmatched segment when there is nothing to
 * highlight, so callers can render the result unconditionally.
 */
export const splitTextOnMatches = (
  text: string,
  query: string
): TextSegment[] => {
  const ranges = findHighlightRanges(text, query);
  if (ranges.length === 0) return [{ text, isMatch: false }];

  const segments: TextSegment[] = [];
  let cursor = 0;

  ranges.forEach(({ start, end }) => {
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), isMatch: false });
    }
    segments.push({ text: text.slice(start, end), isMatch: true });
    cursor = end;
  });

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), isMatch: false });
  }

  return segments;
};

/**
 * The one visual treatment for a search match, shared by the editor's
 * decorations and the plain-text `HighlightedText` so the two never drift.
 */
export const SEARCH_HIGHLIGHT_STYLE = {
  backgroundColor: palette.sunflower_400,
  color: palette.gray_1000,
  borderRadius: 4,
} as const;

/**
 * @description counts every occurrence of `query` in `text`, using the same
 * matching as the highlights so the reported number always equals the number of
 * highlights on screen. Returns 0 for queries below the highlight threshold.
 */
export const countMatches = (text: string, query: string): number =>
  findHighlightRanges(text, query).length;
